// Live inbound-SMS events over the existing /api/ws/ringing socket.
// Calls onInbound({id, from_phone, content, received_at, matched_call_id})
// the moment an SMS reply is ingested for the user's domain.
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const wsBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');

// Reply routing: managers see every domain reply; agents only get events
// for replies to SMS they sent themselves.
const MANAGER_ROLES = new Set([
  'master_admin', 'admin', 'manager', 'supervisor',
  'domain_admin', 'domain_supervisor', 'office_manager',
]);

export const useSmsInboundSocket = (onInbound) => {
  const { user, token } = useAuth();
  const cbRef = useRef(onInbound);
  cbRef.current = onInbound;
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    const domainId = user?.domain_id;
    if (!domainId || !token) return undefined;
    let cancelled = false;
    let ws;
    let timer;

    const connect = () => {
      ws = new WebSocket(`${wsBase()}/api/ws/ringing?domain_id=${encodeURIComponent(domainId)}&token=${encodeURIComponent(token)}`);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data?.type === 'sms_inbound') {
            const inb = data.inbound || {};
            const u = userRef.current;
            const isManager = MANAGER_ROLES.has(u?.role);
            const mine = (inb.for_user_id && inb.for_user_id === u?.id)
              || (inb.for_agent_ext && inb.for_agent_ext === String(u?.extension || ''));
            if (isManager || mine) cbRef.current?.(inb);
          }
        } catch (err) {
          console.warn('[useSmsInboundSocket] bad message payload', err);
        }
      };
      ws.onclose = () => {
        if (!cancelled) timer = setTimeout(connect, 4000 + Math.random() * 2000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      try { ws?.close(); } catch { /* noop */ }
    };
  }, [user?.domain_id, token]);
};
